import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { PeriodWithPropertiesWithIncompleteData } from 'lumen/components/market/total-revenue/elegant-date-handling';

interface MarketTotalRevenueElegantDateHandlingBannerSignature {
  Args: {
    closeBanner: () => void;
    nrOfProperties: number;
    nrOfPropertiesWithCompleteData: number;
    periodsWithPropertiesWithIncompleteData: PeriodWithPropertiesWithIncompleteData[];
    showElegantDateHandlingModal: () => void;
  };
}

export default class MarketTotalRevenueElegantDateHandlingBanner extends Component<MarketTotalRevenueElegantDateHandlingBannerSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bannerMessage() {
    const [periodWithIncompleteData] =
      this.args.periodsWithPropertiesWithIncompleteData;
    if (!periodWithIncompleteData) {
      return undefined;
    }

    const translationOptions = {
      referencePeriod: periodWithIncompleteData.periodTranslation,
      nrOfPropertiesWithData: this.args.nrOfPropertiesWithCompleteData,
      nrOfProperties: this.args.nrOfProperties,
    };

    const isMultiplePeriods =
      this.args.periodsWithPropertiesWithIncompleteData.length > 1;
    if (isMultiplePeriods) {
      return this.intl.t(
        'total_revenue.incomplete_data.multiple_periods',
        translationOptions,
      );
    }

    const isCurrentPeriod = periodWithIncompleteData.id === 'current';
    if (isCurrentPeriod) {
      return this.intl.t(
        'total_revenue.incomplete_data.current_period',
        translationOptions,
      );
    }

    return this.intl.t(
      'total_revenue.incomplete_data.reference_period',
      translationOptions,
    );
  }

  <template>
    {{#if this.bannerMessage}}
      <PrismBanner
        @bannerMessage={{this.bannerMessage}}
        @bannerStyle="warning"
        @close={{@closeBanner}}
        @hasCta={{true}}
        @icon="warning"
        @isOneLine={{true}}
        @primaryAction={{hash
          action=@showElegantDateHandlingModal
          label=(t "total_revenue.see_affected_properties")
        }}
        data-test-id="elegant-date-handling-banner"
      />
    {{/if}}
  </template>
}
